import React from 'react';
import {Link} from 'react-router-dom';
import {AdminSignup} from '../../domain/admin';
import {SignupStatusBadge} from './AdminBadges';
import {EmptyState, ErrorState, LoadingState, PaddleLink, Timestamp} from './AdminPrimitives';

interface AdminSignupsTableProps {
  signups: AdminSignup[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Shapes the empty state — "no failures" is good news, "no results" is not. */
  status: string;
}

const headerCellClass = 'px-4 py-2 font-medium';
const cellClass = 'px-4 py-2.5 align-top';

/**
 * The signup queue.
 *
 * A `failed` row is a paying customer with no account, so each one gets the thing that resolves it:
 * a link to the user once provisioning has produced one (where the setup email can be resent), and
 * the Paddle records when it has not, because until the account exists there is nothing in this app
 * to act on.
 */
const AdminSignupsTable: React.FC<AdminSignupsTableProps> = ({
  signups,
  isLoading,
  error,
  onRetry,
  status,
}) => {
  if (error && signups.length === 0) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (isLoading && signups.length === 0) {
    return <LoadingState message="Loading signups…" />;
  }

  if (signups.length === 0) {
    return (
      <EmptyState
        message={status === 'failed' ? 'No failed signups.' : 'Nothing here.'}
        hint={
          status === 'failed'
            ? 'Nobody has paid without getting an account.'
            : undefined
        }
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[960px] text-left text-xs">
        <thead>
          <tr className="border-b border-gray-800 bg-gray-950/40 text-[11px] uppercase tracking-wide text-gray-500">
            <th scope="col" className={headerCellClass}>Email</th>
            <th scope="col" className={headerCellClass}>Status</th>
            <th scope="col" className={headerCellClass}>Started</th>
            <th scope="col" className={headerCellClass}>Provisioned</th>
            <th scope="col" className={headerCellClass}>Link sent</th>
            <th scope="col" className={headerCellClass}>Expires</th>
            <th scope="col" className={headerCellClass}>Paddle</th>
            <th scope="col" className={headerCellClass} />
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-800/60">
          {signups.map(signup => (
            <tr key={signup.id} className="align-top">
              <td className={`${cellClass} max-w-[260px]`}>
                <span className="block truncate text-gray-100" title={signup.email}>
                  {signup.email}
                </span>
                {signup.name && (
                  <span className="block truncate text-[11px] text-gray-500">{signup.name}</span>
                )}
                {signup.lastError && (
                  <p className="mt-1 max-w-[240px] text-[11px] text-red-300">{signup.lastError}</p>
                )}
              </td>

              <td className={cellClass}>
                <SignupStatusBadge status={signup.status} />
              </td>

              <td className={`${cellClass} text-gray-400`}>
                <Timestamp iso={signup.createdAt} />
              </td>

              <td className={`${cellClass} text-gray-400`}>
                <Timestamp iso={signup.provisionedAt} fallback="Not yet" />
              </td>

              <td className={`${cellClass} text-gray-400`}>
                <Timestamp iso={signup.loginLinkSentAt} fallback="Not yet" />
              </td>

              <td className={`${cellClass} text-gray-400`}>
                <Timestamp iso={signup.expiresAt} />
              </td>

              <td className={cellClass}>
                <div className="space-y-0.5">
                  <PaddleLink kind="customer" id={signup.paddleCustomerId} label="Customer" />
                  <div />
                  <PaddleLink kind="subscription" id={signup.paddleSubscriptionId} label="Subscription" />
                </div>
              </td>

              <td className={`${cellClass} text-right`}>
                {signup.userId ? (
                  <Link
                    to={`/admin/users/${signup.userId}`}
                    className="whitespace-nowrap text-[11px] text-indigo-400 hover:text-indigo-300"
                    title="Open the account — resend their setup email from there"
                  >
                    Open account
                  </Link>
                ) : (
                  <span
                    className="whitespace-nowrap text-[11px] text-gray-600"
                    title="No account exists yet, so there is nothing in the app to act on. Provisioning is retried by cron."
                  >
                    No account
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminSignupsTable;
